/**
 * src/lib/inflightAnalyseFitMarker.js — RC 1.49.5 L-3.
 *
 * Mirror-marker for in-flight Analyse Fit runs. Closes the
 * remount-recovery gap: if the user backgrounds the wizard while
 * Analyse Fit is running, the polling AbortController fires on
 * unmount and the result silently lands in `job_scores` server-side.
 * Without this marker, on remount the wizard renders an empty
 * Step 3 panel until the user manually clicks "Re-run Analyse Fit"
 * (which then cache-hits at zero credits — billing is correct,
 * UX is not).
 *
 * Strategy:
 *   • At `runAnalyseFitOnSelected()` start, write a sessionStorage
 *     marker keyed by `(userId, jobId)` carrying the
 *     `(sourceType, sourceId, submittedAtMs)` triple.
 *   • On wizard remount, if marker exists AND no fresh
 *     `step3LastResult` is restored:
 *       - Set `step3FitRunning(true)` so Next stays disabled.
 *       - Poll `/api/jobs/{id}/best-fit` periodically (every 8s,
 *         budget 100s — same shape as `withTimeoutRecovery`).
 *       - When the matching row appears in `job_scores`, P0-4's
 *         `fitSnapshot`-driven hydrator surfaces it as
 *         `step3LastResult` automatically. Clear the marker.
 *       - On budget exhaustion, clear `step3FitRunning(false)` and
 *         the marker; user can re-click (cache-hit, free).
 *
 * Hard rails:
 *   • No new endpoint. No schema change. No billing change.
 *   • Re-poll uses GET /best-fit only (read-only, no charge).
 *   • Will NEVER re-dispatch /analyze-fit automatically — that is
 *     the user's explicit action, not a recovery action.
 *   • Cleared on success or budget exhaustion.
 *   • TTL aligned to 10 min (matches CV/letter markers).
 */

const KEY_PREFIX = 'inflight-fit';

export const INFLIGHT_FIT_RECOVERY_WINDOW_MS = 10 * 60 * 1000;

function _key(userId, jobId) {
  return `${KEY_PREFIX}:${userId || ''}:${jobId || ''}`;
}

export function writeInflightFitMarker(userId, jobId, { sourceType, sourceId }) {
  if (!jobId || !sourceType || !sourceId) return;
  try {
    sessionStorage.setItem(
      _key(userId, jobId),
      JSON.stringify({
        submittedAtMs: Date.now(),
        sourceType,
        sourceId,
        startedAt: new Date().toISOString(),
      }),
    );
  } catch { /* sessionStorage unavailable — non-fatal */ }
}

export function clearInflightFitMarker(userId, jobId) {
  if (!jobId) return;
  try { sessionStorage.removeItem(_key(userId, jobId)); } catch { /* */ }
}

export function readInflightFitMarker(userId, jobId) {
  if (!jobId) return null;
  try {
    const raw = sessionStorage.getItem(_key(userId, jobId));
    if (!raw) return null;
    const payload = JSON.parse(raw);
    if (!payload || typeof payload !== 'object') return null;
    if (!payload.sourceType || !payload.sourceId || !payload.submittedAtMs) return null;
    if ((Date.now() - Number(payload.submittedAtMs)) > INFLIGHT_FIT_RECOVERY_WINDOW_MS) {
      // Stale → purge so callers don't wait on a dead marker.
      try { sessionStorage.removeItem(_key(userId, jobId)); } catch { /* */ }
      return null;
    }
    return payload;
  } catch { return null; }
}
